import React, { useState, useRef, useEffect, useMemo } from 'react';
import './ColoringApp.css';

const ColoringApp = () => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const saveFileInputRef = useRef(null);
  const loadFileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('brush');
  const [color, setColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(5);
  const [pencilSize, setPencilSize] = useState(2);
  const [eraserSize, setEraserSize] = useState(10);
  const [spraySize, setSpraySize] = useState(20);
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [cursorType, setCursorType] = useState('crosshair'); // 'crosshair' или 'circle'
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [hue, setHue] = useState(0); // Оттенок для расширенной палитры
  const [saturation, setSaturation] = useState(1); // Насыщенность
  const [value, setValue] = useState(1); // Яркость
  const [isEyedropperActive, setIsEyedropperActive] = useState(false);

  const [layers, setLayers] = useState({ 
    base: null,      // Оригинальное изображение
    shadows: null,   // Слой с тенями (создается из оригинала)
    drawing: null    // Слой для рисования
  });

  // История действий для undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyRef = useRef({ history: [], historyIndex: -1 });
  const maxHistorySize = 50; // Максимальный размер истории

  // Палитра предустановленных цветов
  const colorPalette = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080',
    '#FFC0CB', '#A52A2A', '#808080', '#008000', '#000080',
    '#FFD700'
  ];

  // Функция для преобразования HSV в RGB
  const hsvToRgb = (h, s, v) => {
    h = h % 360;
    const c = v * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = v - c;
    let r, g, b;

    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0;
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x;
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c;
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c;
    } else {
      r = c; g = 0; b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // Функция для преобразования RGB в HSV
  const rgbToHsv = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
      if (max === r) {
        h = ((g - b) / delta) % 6;
      } else if (max === g) {
        h = (b - r) / delta + 2;
      } else {
        h = (r - g) / delta + 4;
      }
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    const s = max === 0 ? 0 : delta / max;
    const v = max;

    return { h, s, v };
  };

  // Обновление HSV при изменении цвета
  useEffect(() => {
    const hsv = rgbToHsv(color);
    setHue(hsv.h);
    setSaturation(hsv.s);
    setValue(hsv.v);
  }, [color]);

  // Обработчик клика по расширенной палитре
  const handleAdvancedColorPick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(rect.width, Math.max(0, e.clientX - rect.left));
    const y = Math.min(rect.height, Math.max(0, e.clientY - rect.top));
    const newSaturation = x / rect.width;
    const newValue = 1 - (y / rect.height);
    setSaturation(newSaturation);
    setValue(newValue);
    const newColor = hsvToRgb(hue, newSaturation, newValue);
    setColor(newColor);
  };

  // Обработчик изменения оттенка
  const handleHueChange = (newHue) => {
    setHue(newHue);
    const newColor = hsvToRgb(newHue, saturation, value);
    setColor(newColor);
  };

  // Функция для использования пипетки (EyeDropper API)
  const useEyedropper = async () => {
    
    // Проверяем поддержку EyeDropper API
    if ('EyeDropper' in window) {
      try {
        const eyeDropper = new EyeDropper();
        const result = await eyeDropper.open();
        if (result.sRGBHex) {
          setColor(result.sRGBHex);
        }
      } catch (err) {
        // Пользователь отменил выбор или произошла ошибка
        console.log('EyeDropper cancelled or error:', err);
      }
    } else {
      // Fallback: используем альтернативный метод через canvas
      setIsEyedropperActive(true);
      const originalCursor = document.body.style.cursor;
      document.body.style.cursor = 'crosshair';
      
      const handleEyedropperClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        try {
          // Пытаемся получить цвет из canvas приложения
          if (layers.base) {
            const appCanvas = canvasRef.current;
            if (appCanvas) {
              const rect = appCanvas.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;
              
              // Проверяем, что клик был по canvas
              if (x >= 0 && x < rect.width && y >= 0 && y < rect.height) {
                // Используем существующую функцию для преобразования координат
                const { x: canvasX, y: canvasY } = getCanvasCoordinates(e.clientX, e.clientY);
                
                // Создаем временный canvas для получения цвета из всех слоев
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = layers.base.width;
                tempCanvas.height = layers.base.height;
                const tempCtx = tempCanvas.getContext('2d');
                
                // Рисуем все слои
                tempCtx.drawImage(layers.base, 0, 0);
                if (layers.shadows) {
                  tempCtx.globalAlpha = 0.3;
                  tempCtx.drawImage(layers.shadows, 0, 0);
                  tempCtx.globalAlpha = 1.0;
                }
                if (layers.drawing) {
                  tempCtx.globalCompositeOperation = 'multiply';
                  tempCtx.drawImage(layers.drawing, 0, 0);
                  tempCtx.globalCompositeOperation = 'source-over';
                }
                
                // Получаем цвет пикселя
                const pixelX = Math.floor(canvasX);
                const pixelY = Math.floor(canvasY);
                
                if (pixelX >= 0 && pixelX < tempCanvas.width && pixelY >= 0 && pixelY < tempCanvas.height) {
                  const imageData = tempCtx.getImageData(pixelX, pixelY, 1, 1);
                  const [r, g, b] = imageData.data;
                  const hexColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                  setColor(hexColor);
                }
              } else {
                // Если клик не по canvas, пытаемся получить цвет из элемента
                const element = document.elementFromPoint(e.clientX, e.clientY);
                if (element && element !== appCanvas) {
                  const computedStyle = window.getComputedStyle(element);
                  const bgColor = computedStyle.backgroundColor;
                  if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
                    const rgb = bgColor.match(/\d+/g);
                    if (rgb && rgb.length >= 3) {
                      const hex = `#${parseInt(rgb[0]).toString(16).padStart(2, '0')}${parseInt(rgb[1]).toString(16).padStart(2, '0')}${parseInt(rgb[2]).toString(16).padStart(2, '0')}`;
                      setColor(hex);
                    }
                  }
                }
              }
            }
          } else {
            // Если нет canvas, используем цвет из элемента
            const element = document.elementFromPoint(e.clientX, e.clientY);
            if (element) {
              const computedStyle = window.getComputedStyle(element);
              const bgColor = computedStyle.backgroundColor;
              if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
                const rgb = bgColor.match(/\d+/g);
                if (rgb && rgb.length >= 3) {
                  const hex = `#${parseInt(rgb[0]).toString(16).padStart(2, '0')}${parseInt(rgb[1]).toString(16).padStart(2, '0')}${parseInt(rgb[2]).toString(16).padStart(2, '0')}`;
                  setColor(hex);
                }
              }
            }
          }
        } catch (err) {
          console.error('Error picking color:', err);
        }
        
        // Отключаем режим пипетки
        setIsEyedropperActive(false);
        document.body.style.cursor = originalCursor;
        document.removeEventListener('click', handleEyedropperClick, true);
      };
      
      document.addEventListener('click', handleEyedropperClick, true);
    }
  };

  // Функция для создания слоя теней из оригинального изображения
  const createShadowLayer = (baseCanvas) => {
    const shadowCanvas = document.createElement('canvas');
    shadowCanvas.width = baseCanvas.width;
    shadowCanvas.height = baseCanvas.height;
    const ctx = shadowCanvas.getContext('2d');
    
    // Рисуем оригинальное изображение
    ctx.drawImage(baseCanvas, 0, 0);
    
    // Применяем эффекты для создания теней
    const imageData = ctx.getImageData(0, 0, shadowCanvas.width, shadowCanvas.height);
    const data = imageData.data;
    
    // Увеличиваем контраст и делаем тени более выраженными
    for (let i = 0; i < data.length; i += 4) {
      const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
      
      // Если пиксель темный (теневой), усиливаем его
      if (brightness < 128) {
        data[i] = Math.max(0, data[i] - 30);     // R
        data[i + 1] = Math.max(0, data[i + 1] - 30); // G
        data[i + 2] = Math.max(0, data[i + 2] - 30); // B
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    return shadowCanvas;
  };

  // Сохранение состояния в историю
  const saveToHistory = (drawingCanvas) => {
    if (!drawingCanvas) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = drawingCanvas.width;
    canvas.height = drawingCanvas.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(drawingCanvas, 0, 0);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    const currentIndex = historyRef.current.historyIndex;
    const newHistory = historyRef.current.history.slice(0, currentIndex + 1);
    newHistory.push(imageData);
    
    // Ограничиваем размер истории
    if (newHistory.length > maxHistorySize) {
      newHistory.shift();
    }
    
    const newIndex = Math.min(currentIndex + 1, newHistory.length - 1);
    historyRef.current = { history: newHistory, historyIndex: newIndex };
    setHistory(newHistory);
    setHistoryIndex(newIndex);
  };

  // Восстановление состояния из истории
  const restoreFromHistory = (imageData) => {
    if (!imageData || !layers.base) return;
    
    const drawingCanvas = document.createElement('canvas');
    drawingCanvas.width = layers.base.width;
    drawingCanvas.height = layers.base.height;
    const ctx = drawingCanvas.getContext('2d');
    ctx.putImageData(imageData, 0, 0);
    
    setLayers(prev => ({ ...prev, drawing: drawingCanvas }));
  };

  // Undo - отменить последнее действие
  const undo = () => {
    const currentIndex = historyRef.current.historyIndex;
    const currentHistory = historyRef.current.history;
    
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      historyRef.current.historyIndex = newIndex;
      setHistoryIndex(newIndex);
      restoreFromHistory(currentHistory[newIndex]);
    } else if (currentIndex === 0) {
      // Очищаем слой рисования
      historyRef.current.historyIndex = -1;
      setHistoryIndex(-1);
      setLayers(prev => ({ ...prev, drawing: null }));
    }
  };

  // Redo - повторить отмененное действие
  const redo = () => {
    const currentIndex = historyRef.current.historyIndex;
    const currentHistory = historyRef.current.history;
    
    if (currentIndex < currentHistory.length - 1) {
      const newIndex = currentIndex + 1;
      historyRef.current.historyIndex = newIndex;
      setHistoryIndex(newIndex);
      restoreFromHistory(currentHistory[newIndex]);
    }
  };

  // Вспомогательная функция для установки размеров canvas на основе контейнера
  const setCanvasSizeToContainer = (canvas, img) => {
    if (!canvas) return;
    
    const container = canvas.parentElement;
    if (container) {
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const padding = 5;
      const maxWidth = containerWidth - padding * 2;
      const maxHeight = containerHeight - padding * 2;
      
      // Вычисляем масштаб для contain
      const scaleX = maxWidth / img.width;
      const scaleY = maxHeight / img.height;
      const initialScale = Math.min(scaleX, scaleY);
      const finalScale = Math.max(0.1, initialScale);
      
      // Устанавливаем canvas в размеры контейнера
      canvas.width = maxWidth;
      canvas.height = maxHeight;
      
      return { scale: finalScale, maxWidth, maxHeight };
    } else {
      // Fallback
      const maxHeight = window.innerHeight - 100;
      const scaleByHeight = maxHeight / img.height;
      const finalScale = Math.max(0.1, Math.min(scaleByHeight, 1));
      canvas.width = window.innerWidth - 300;
      canvas.height = maxHeight;
      return { scale: finalScale, maxWidth: canvas.width, maxHeight };
    }
  };

  // Обновленная функция инициализации слоев
  const initializeLayers = (img) => {
    // Используем исходные размеры изображения
    const baseCanvas = document.createElement('canvas');
    baseCanvas.width = img.width;
    baseCanvas.height = img.height;
    const baseCtx = baseCanvas.getContext('2d');
    baseCtx.drawImage(img, 0, 0);
    
    const shadowLayer = createShadowLayer(baseCanvas);
    
    // Устанавливаем размер основного canvas под размер контейнера
    const canvas = canvasRef.current;
    if (canvas) {
      // Вычисляем масштаб для contain (чтобы изображение полностью поместилось)
      setTimeout(() => {
        const sizeInfo = setCanvasSizeToContainer(canvas, img);
        if (sizeInfo) {
          const { scale: finalScale, maxWidth, maxHeight } = sizeInfo;
          
          // Вычисляем размеры масштабированного изображения в координатах canvas
          const scaledWidth = img.width * finalScale;
          const scaledHeight = img.height * finalScale;
          
          // Вычисляем offset для центрирования изображения в canvas
          const offsetX = (maxWidth - scaledWidth) / 2;
          const offsetY = (maxHeight - scaledHeight) / 2;
          
          setScale(finalScale);
          setOffset({ x: offsetX, y: offsetY });
        }
      }, 100); // Задержка, чтобы контейнер точно отрендерился
    }
    
    setLayers({ 
      base: baseCanvas, 
      shadows: shadowLayer,
      drawing: null 
    });
    
    // Сбрасываем историю при загрузке нового изображения
    historyRef.current = { history: [], historyIndex: -1 };
    setHistory([]);
    setHistoryIndex(-1);
  };

  // Функция для преобразования координат мыши в координаты canvas
  const getCanvasCoordinates = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    // Получаем координаты canvas элемента относительно viewport
    const canvasRect = canvas.getBoundingClientRect();
    
    // Координаты мыши относительно левого верхнего угла canvas элемента (в отображаемых координатах)
    const mouseX = clientX - canvasRect.left;
    const mouseY = clientY - canvasRect.top;
    
    // Вычисляем CSS масштаб (соотношение между внутренними и отображаемыми размерами)
    const cssScaleX = canvasRect.width / canvas.width;
    const cssScaleY = canvasRect.height / canvas.height;
    
    // Преобразуем координаты мыши из отображаемых координат в внутренние координаты canvas
    // (до применения transform)
    const canvasX = mouseX / cssScaleX;
    const canvasY = mouseY / cssScaleY;
    
    // В redrawCanvas применяется transform в следующем порядке:
    // 1. ctx.translate(offset.x, offset.y) - смещает начало координат на offset
    // 2. ctx.scale(scale, scale) - масштабирует координаты
    //
    // Математически это означает:
    // Точка (x, y) в исходной системе координат canvas преобразуется в:
    // x' = offset.x + x * scale
    // y' = offset.y + y * scale
    //
    // Где (x', y') - координаты в системе координат canvas элемента (внутренние координаты)
    //
    // Обратное преобразование:
    // x = (x' - offset.x) / scale
    // y = (y' - offset.y) / scale
    //
    // Где x' = canvasX, y' = canvasY
    // offset уже в внутренних координатах canvas
    const x = (canvasX - offset.x) / scale;
    const y = (canvasY - offset.y) / scale;
    
    return { x, y };
  };

  // Обновленная функция перерисовки
  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !layers.base) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.save();
    // Применяем transform: сначала смещение, затем масштаб
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);
    
    // Рисуем базовое изображение
    ctx.drawImage(layers.base, 0, 0);
    
    // Рисуем слой теней с пониженной прозрачностью
    if (layers.shadows) {
      ctx.globalAlpha = 0.3; // Делаем тени полупрозрачными
      ctx.drawImage(layers.shadows, 0, 0);
      ctx.globalAlpha = 1.0;
    }
    
    // Рисуем слой рисования
    if (layers.drawing) {
      ctx.globalCompositeOperation = 'multiply'; // Сохраняем тени при рисовании
      ctx.drawImage(layers.drawing, 0, 0);
    }
    
    ctx.restore();
  };

  // Загрузка изображения
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setOriginalFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setImage(img);
          initializeLayers(img);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
    // Сбрасываем input, чтобы можно было загрузить тот же файл снова
    event.target.value = '';
  };

  // Масштабирование
  const handleZoom = (delta, clientX, clientY) => {
    if (!layers.base) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Координаты мыши в отображаемых координатах
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    
    // Вычисляем CSS масштаб
    const cssScaleX = rect.width / canvas.width;
    const cssScaleY = rect.height / canvas.height;
    
    // Преобразуем координаты мыши в внутренние координаты canvas
    const canvasX = mouseX / cssScaleX;
    const canvasY = mouseY / cssScaleY;
    
    const newScale = Math.max(0.1, Math.min(5, scale + delta * 0.1));
    
    // Вычисляем координаты точки в системе координат изображения
    const imageX = (canvasX - offset.x) / scale;
    const imageY = (canvasY - offset.y) / scale;
    
    // Вычисляем новый offset для сохранения позиции точки под курсором
    const newOffsetX = canvasX - imageX * newScale;
    const newOffsetY = canvasY - imageY * newScale;
    
    setScale(newScale);
    setOffset({ x: newOffsetX, y: newOffsetY });
  };

  // Создание слоя рисования
  const createDrawingLayer = () => {
    if (!layers.base || layers.drawing) return layers.drawing;
    
    const drawingCanvas = document.createElement('canvas');
    drawingCanvas.width = layers.base.width;
    drawingCanvas.height = layers.base.height;
    setLayers(prev => ({ ...prev, drawing: drawingCanvas }));
    return drawingCanvas;
  };

  // Начало рисования
  const startDrawing = (e) => {
    // Если изображение не загружено, открываем диалог загрузки
    if (!layers.base && e.button === 0 && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }
    
    if (!layers.base) return;
    
    if (e.button === 1 || (e.button === 0 && e.ctrlKey)) {
      setIsDragging(true);
      setLastPos({ x: e.clientX, y: e.clientY });
      return;
    }
    
    if (e.button !== 0) return;
    
    setIsDrawing(true);
    
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    
    // Создаем слой рисования при необходимости
    let drawingLayer = layers.drawing;
    if (!drawingLayer) {
      drawingLayer = createDrawingLayer();
    }
    
    if (drawingLayer) {
      const drawingCtx = drawingLayer.getContext('2d');
      drawingCtx.beginPath();
      drawingCtx.moveTo(x, y);
    }
  };

  // Процесс рисования
  const draw = (e) => {
    if (!layers.base) return;
    
    if (isDragging) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const cssScaleX = rect.width / canvas.width;
      const cssScaleY = rect.height / canvas.height;
      
      // Преобразуем дельту из отображаемых координат в внутренние координаты canvas
      const deltaX = (e.clientX - lastPos.x) / cssScaleX;
      const deltaY = (e.clientY - lastPos.y) / cssScaleY;
      
      setOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      setLastPos({ x: e.clientX, y: e.clientY });
      redrawCanvas();
      return;
    }
    
    if (!isDrawing) return;
    
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    
    // Убедимся, что слой рисования существует
    let drawingLayer = layers.drawing;
    if (!drawingLayer) {
      drawingLayer = createDrawingLayer();
      if (!drawingLayer) return;
    }
    
    const drawingCtx = drawingLayer.getContext('2d');
    
    switch (tool) {
      case 'brush':
        drawingCtx.globalCompositeOperation = 'source-over';
        drawingCtx.lineWidth = brushSize;
        drawingCtx.lineCap = 'round';
        drawingCtx.strokeStyle = color;
        drawingCtx.lineTo(x, y);
        drawingCtx.stroke();
        drawingCtx.beginPath();
        drawingCtx.moveTo(x, y);
        break;
      case 'pencil':
        drawingCtx.globalCompositeOperation = 'source-over';
        drawingCtx.lineWidth = pencilSize;
        drawingCtx.lineCap = 'round';
        drawingCtx.strokeStyle = color;
        drawingCtx.lineTo(x, y);
        drawingCtx.stroke();
        drawingCtx.beginPath();
        drawingCtx.moveTo(x, y);
        break;
      case 'eraser':
        drawingCtx.globalCompositeOperation = 'destination-out';
        drawingCtx.lineWidth = eraserSize;
        drawingCtx.lineCap = 'round';
        drawingCtx.strokeStyle = 'rgba(0,0,0,1)';
        drawingCtx.lineTo(x, y);
        drawingCtx.stroke();
        drawingCtx.beginPath();
        drawingCtx.moveTo(x, y);
        break;
      case 'spray':
        // Распылитель - рисуем случайные точки в области
        drawingCtx.globalCompositeOperation = 'source-over';
        drawingCtx.fillStyle = color;
        const sprayRadius = spraySize / 2;
        const pointsCount = Math.floor(spraySize / 2);
        
        for (let i = 0; i < pointsCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const distance = Math.random() * sprayRadius;
          const px = x + Math.cos(angle) * distance;
          const py = y + Math.sin(angle) * distance;
          
          drawingCtx.fillRect(px, py, 1, 1);
        }
        break;
    }
    
    // Перерисовываем canvas после каждого движения
    redrawCanvas();
  };

  // Завершение рисования
  const stopDrawing = () => {
    if (isDrawing && layers.drawing) {
      // Сохраняем состояние в историю после завершения рисования
      saveToHistory(layers.drawing);
    }
    
    setIsDrawing(false);
    setIsDragging(false);
    
    if (layers.drawing) {
      const drawingCtx = layers.drawing.getContext('2d');
      drawingCtx.beginPath();
    }
  };

  // Очистка слоя рисования
  const clearDrawing = () => {
    if (layers.drawing) {
      // Сохраняем текущее состояние перед очисткой
      saveToHistory(layers.drawing);
      
      const drawingCtx = layers.drawing.getContext('2d');
      drawingCtx.clearRect(0, 0, layers.drawing.width, layers.drawing.height);
      redrawCanvas();
      
      // Сохраняем пустое состояние
      saveToHistory(layers.drawing);
    }
  };

  // Сохранение изображения
  const saveImage = () => {
    if (!layers.base) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = layers.base.width;
    canvas.height = layers.base.height;
    const ctx = canvas.getContext('2d');
    
    // Сначала рисуем базовое изображение
    ctx.drawImage(layers.base, 0, 0);
    
    // Затем рисуем слой теней с прозрачностью для видимости контуров
    if (layers.shadows) {
      ctx.globalAlpha = 0.3;
      ctx.drawImage(layers.shadows, 0, 0);
      ctx.globalAlpha = 1.0;
    }
    
    // И наконец рисуем слой рисования с режимом multiply, чтобы сохранить контуры
    if (layers.drawing) {
      ctx.globalCompositeOperation = 'multiply';
      ctx.drawImage(layers.drawing, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
    }
    
    const link = document.createElement('a');
    link.download = 'coloring-result.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Сохранение состояния в localStorage
  const saveStateToLocalStorage = () => {
    if (!layers.base) return;
    
    try {
      const state = {
        base: layers.base.toDataURL('image/png'),
        shadows: layers.shadows ? layers.shadows.toDataURL('image/png') : null,
        drawing: layers.drawing ? layers.drawing.toDataURL('image/png') : null,
        scale,
        offset,
        originalFileName,
        timestamp: new Date().toISOString()
      };
      
      const stateString = JSON.stringify(state);
      const sizeInMB = new Blob([stateString]).size / (1024 * 1024);
      
      // Проверяем размер данных (localStorage обычно ограничен ~5-10MB)
      if (sizeInMB > 5) {
        const shouldContinue = window.confirm(
          `Размер данных для сохранения: ${sizeInMB.toFixed(2)}MB. Это может превысить лимит localStorage. ` +
          `Рекомендуется использовать экспорт в файл. Продолжить сохранение в localStorage?`
        );
        if (!shouldContinue) {
          return;
        }
      }
      
      localStorage.setItem('coloringAppState', stateString);
      alert('Состояние сохранено! Вы можете вернуться к работе позже.');
    } catch (error) {
      console.error('Ошибка при сохранении состояния:', error);
      if (error.name === 'QuotaExceededError') {
        alert('Недостаточно места в localStorage. Используйте экспорт в файл.');
      } else {
        alert('Ошибка при сохранении состояния. Попробуйте экспортировать проект в файл.');
      }
    }
  };

  // Загрузка состояния из localStorage
  const loadStateFromLocalStorage = () => {
    try {
      const savedState = localStorage.getItem('coloringAppState');
      if (!savedState) {
        alert('Сохраненное состояние не найдено.');
        return;
      }
      
      const state = JSON.parse(savedState);
      
      // Создаем изображения из base64
      const baseImg = new Image();
      baseImg.onload = () => {
        const baseCanvas = document.createElement('canvas');
        baseCanvas.width = baseImg.width;
        baseCanvas.height = baseImg.height;
        const baseCtx = baseCanvas.getContext('2d');
        baseCtx.drawImage(baseImg, 0, 0);
        
        let shadowCanvas = null;
        if (state.shadows) {
          const shadowImg = new Image();
          shadowImg.onload = () => {
            shadowCanvas = document.createElement('canvas');
            shadowCanvas.width = shadowImg.width;
            shadowCanvas.height = shadowImg.height;
            const shadowCtx = shadowCanvas.getContext('2d');
            shadowCtx.drawImage(shadowImg, 0, 0);
            
            let drawingCanvas = null;
            if (state.drawing) {
              const drawingImg = new Image();
              drawingImg.onload = () => {
                drawingCanvas = document.createElement('canvas');
                drawingCanvas.width = drawingImg.width;
                drawingCanvas.height = drawingImg.height;
                const drawingCtx = drawingCanvas.getContext('2d');
                drawingCtx.drawImage(drawingImg, 0, 0);
                
                // Устанавливаем canvas размеры
                const canvas = canvasRef.current;
                if (canvas) {
                  setTimeout(() => {
                    const sizeInfo = setCanvasSizeToContainer(canvas, baseImg);
                    if (sizeInfo) {
                      const { scale: newScale, maxWidth, maxHeight } = sizeInfo;
                      const scaledWidth = baseImg.width * newScale;
                      const scaledHeight = baseImg.height * newScale;
                      const offsetX = (maxWidth - scaledWidth) / 2;
                      const offsetY = (maxHeight - scaledHeight) / 2;
                      setScale(newScale);
                      setOffset({ x: offsetX, y: offsetY });
                    }
                  }, 100);
                }
                
                setImage(baseImg);
                setLayers({ base: baseCanvas, shadows: shadowCanvas, drawing: drawingCanvas });
                setOriginalFileName(state.originalFileName || null);
                
                // Сбрасываем историю
                historyRef.current = { history: [], historyIndex: -1 };
                setHistory([]);
                setHistoryIndex(-1);
                
                // Перерисовываем canvas
                setTimeout(() => {
                  redrawCanvas();
                }, 200);
              };
              drawingImg.src = state.drawing;
            } else {
              // Устанавливаем canvas размеры
              const canvas = canvasRef.current;
              if (canvas) {
                setTimeout(() => {
                  const sizeInfo = setCanvasSizeToContainer(canvas, baseImg);
                  if (sizeInfo) {
                    const { scale: newScale, maxWidth, maxHeight } = sizeInfo;
                    const scaledWidth = baseImg.width * newScale;
                    const scaledHeight = baseImg.height * newScale;
                    const offsetX = (maxWidth - scaledWidth) / 2;
                    const offsetY = (maxHeight - scaledHeight) / 2;
                    setScale(newScale);
                    setOffset({ x: offsetX, y: offsetY });
                  }
                }, 100);
              }
              
              setImage(baseImg);
              setLayers({ base: baseCanvas, shadows: shadowCanvas, drawing: null });
              setOriginalFileName(state.originalFileName || null);
              
              // Сбрасываем историю
              historyRef.current = { history: [], historyIndex: -1 };
              setHistory([]);
              setHistoryIndex(-1);
              
              // Перерисовываем canvas
              setTimeout(() => {
                redrawCanvas();
              }, 200);
            }
          };
          shadowImg.src = state.shadows;
        } else {
          // Устанавливаем canvas размеры
          const canvas = canvasRef.current;
          if (canvas) {
            setTimeout(() => {
              const sizeInfo = setCanvasSizeToContainer(canvas, baseImg);
              if (sizeInfo) {
                const { scale: newScale, maxWidth, maxHeight } = sizeInfo;
                const scaledWidth = baseImg.width * newScale;
                const scaledHeight = baseImg.height * newScale;
                const offsetX = (maxWidth - scaledWidth) / 2;
                const offsetY = (maxHeight - scaledHeight) / 2;
                setScale(newScale);
                setOffset({ x: offsetX, y: offsetY });
              }
            }, 100);
          }
          
          setImage(baseImg);
          setLayers({ base: baseCanvas, shadows: null, drawing: null });
          setOriginalFileName(state.originalFileName || null);
          
          // Сбрасываем историю
          historyRef.current = { history: [], historyIndex: -1 };
          setHistory([]);
          setHistoryIndex(-1);
          
          // Перерисовываем canvas
          setTimeout(() => {
            redrawCanvas();
          }, 200);
        }
      };
      baseImg.src = state.base;
    } catch (error) {
      console.error('Ошибка при загрузке состояния:', error);
      alert('Ошибка при загрузке состояния.');
    }
  };

  // Экспорт проекта в файл
  const exportProject = () => {
    if (!layers.base) return;
    
    try {
      const projectData = {
        base: layers.base.toDataURL('image/png'),
        shadows: layers.shadows ? layers.shadows.toDataURL('image/png') : null,
        drawing: layers.drawing ? layers.drawing.toDataURL('image/png') : null,
        scale,
        offset,
        originalFileName,
        timestamp: new Date().toISOString(),
        version: '1.0'
      };
      
      const dataStr = JSON.stringify(projectData);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalFileName ? `${originalFileName.replace(/\.[^/.]+$/, '')}_project.json` : 'coloring-project.json';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка при экспорте проекта:', error);
      alert('Ошибка при экспорте проекта.');
    }
  };

  // Импорт проекта из файла
  const handleProjectImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const projectData = JSON.parse(e.target.result);
        
        // Проверяем версию
        if (!projectData.base) {
          alert('Неверный формат файла проекта.');
          return;
        }
        
        // Загружаем изображения
        const baseImg = new Image();
        baseImg.onload = () => {
          const baseCanvas = document.createElement('canvas');
          baseCanvas.width = baseImg.width;
          baseCanvas.height = baseImg.height;
          const baseCtx = baseCanvas.getContext('2d');
          baseCtx.drawImage(baseImg, 0, 0);
          
          let shadowCanvas = null;
          if (projectData.shadows) {
            const shadowImg = new Image();
            shadowImg.onload = () => {
              shadowCanvas = document.createElement('canvas');
              shadowCanvas.width = shadowImg.width;
              shadowCanvas.height = shadowImg.height;
              const shadowCtx = shadowCanvas.getContext('2d');
              shadowCtx.drawImage(shadowImg, 0, 0);
              
              let drawingCanvas = null;
              if (projectData.drawing) {
                const drawingImg = new Image();
                drawingImg.onload = () => {
                  drawingCanvas = document.createElement('canvas');
                  drawingCanvas.width = drawingImg.width;
                  drawingCanvas.height = drawingImg.height;
                  const drawingCtx = drawingCanvas.getContext('2d');
                  drawingCtx.drawImage(drawingImg, 0, 0);
                  
                  // Устанавливаем canvas размеры
                  const canvas = canvasRef.current;
                  if (canvas) {
                    setTimeout(() => {
                      const sizeInfo = setCanvasSizeToContainer(canvas, baseImg);
                      if (sizeInfo) {
                        const { scale: newScale, maxWidth, maxHeight } = sizeInfo;
                        const scaledWidth = baseImg.width * newScale;
                        const scaledHeight = baseImg.height * newScale;
                        const offsetX = (maxWidth - scaledWidth) / 2;
                        const offsetY = (maxHeight - scaledHeight) / 2;
                        setScale(newScale);
                        setOffset({ x: offsetX, y: offsetY });
                      }
                    }, 100);
                  }
                  
                  setImage(baseImg);
                  setLayers({ base: baseCanvas, shadows: shadowCanvas, drawing: drawingCanvas });
                  setOriginalFileName(projectData.originalFileName || null);
                  
                  // Сбрасываем историю
                  historyRef.current = { history: [], historyIndex: -1 };
                  setHistory([]);
                  setHistoryIndex(-1);
                  
                  // Перерисовываем canvas
                  setTimeout(() => {
                    redrawCanvas();
                  }, 200);
                };
                drawingImg.src = projectData.drawing;
              } else {
                // Устанавливаем canvas размеры
                const canvas = canvasRef.current;
                if (canvas) {
                  setTimeout(() => {
                    const sizeInfo = setCanvasSizeToContainer(canvas, baseImg);
                    if (sizeInfo) {
                      const { scale: newScale, maxWidth, maxHeight } = sizeInfo;
                      const scaledWidth = baseImg.width * newScale;
                      const scaledHeight = baseImg.height * newScale;
                      const offsetX = (maxWidth - scaledWidth) / 2;
                      const offsetY = (maxHeight - scaledHeight) / 2;
                      setScale(newScale);
                      setOffset({ x: offsetX, y: offsetY });
                    }
                  }, 100);
                }
                
                setImage(baseImg);
                setLayers({ base: baseCanvas, shadows: shadowCanvas, drawing: null });
                setOriginalFileName(projectData.originalFileName || null);
                
                // Сбрасываем историю
                historyRef.current = { history: [], historyIndex: -1 };
                setHistory([]);
                setHistoryIndex(-1);
                
                // Перерисовываем canvas
                setTimeout(() => {
                  redrawCanvas();
                }, 200);
              }
            };
            shadowImg.src = projectData.shadows;
          } else {
            // Устанавливаем canvas размеры
            const canvas = canvasRef.current;
            if (canvas) {
              setTimeout(() => {
                const sizeInfo = setCanvasSizeToContainer(canvas, baseImg);
                if (sizeInfo) {
                  const { scale: newScale, maxWidth, maxHeight } = sizeInfo;
                  const scaledWidth = baseImg.width * newScale;
                  const scaledHeight = baseImg.height * newScale;
                  const offsetX = (maxWidth - scaledWidth) / 2;
                  const offsetY = (maxHeight - scaledHeight) / 2;
                  setScale(newScale);
                  setOffset({ x: offsetX, y: offsetY });
                }
              }, 100);
            }
            
            setImage(baseImg);
            setLayers({ base: baseCanvas, shadows: null, drawing: null });
            setOriginalFileName(projectData.originalFileName || null);
            
            // Сбрасываем историю
            historyRef.current = { history: [], historyIndex: -1 };
            setHistory([]);
            setHistoryIndex(-1);
            
            // Перерисовываем canvas
            setTimeout(() => {
              redrawCanvas();
            }, 200);
          }
        };
        baseImg.src = projectData.base;
      } catch (error) {
        console.error('Ошибка при импорте проекта:', error);
        alert('Ошибка при импорте проекта. Неверный формат файла.');
      }
    };
    reader.readAsText(file);
    // Сбрасываем input, чтобы можно было загрузить тот же файл снова
    event.target.value = '';
  };

  // Обработка колеса мыши для зума
  const handleWheel = (e) => {
    e.preventDefault();
    if (layers.base) {
      handleZoom(-Math.sign(e.deltaY), e.clientX, e.clientY);
    }
  };

  // Вычисление стиля курсора (мемоизировано для оптимизации)
  const cursorStyle = useMemo(() => {
    if (!layers.base) return 'pointer';
    if (isDragging) return 'grabbing';
    
    // Получаем размер инструмента
    const toolSize = tool === 'brush' ? brushSize : 
                     tool === 'pencil' ? pencilSize : 
                     tool === 'eraser' ? eraserSize : 
                     tool === 'spray' ? spraySize : 10;
    
    // Получаем масштаб canvas для правильного отображения размера курсора
    const canvas = canvasRef.current;
    let displayScale = 1;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const cssScaleX = rect.width / canvas.width;
      const cssScaleY = rect.height / canvas.height;
      // Используем средний масштаб для курсора
      displayScale = (cssScaleX + cssScaleY) / 2;
    }
    
    // Вычисляем визуальный размер курсора с учетом масштаба
    const visualSize = Math.max(10, toolSize * scale * displayScale);
    
    if (cursorType === 'circle') {
      // Для круга используем кастомный курсор через SVG
      const cursorSize = Math.ceil(visualSize * 2);
      const radius = visualSize;
      const svg = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${cursorSize}" height="${cursorSize}"><circle cx="${cursorSize/2}" cy="${cursorSize/2}" r="${radius}" fill="none" stroke="#000" stroke-width="2"/></svg>`);
      // Hotspot курсора в центре круга
      return `url("data:image/svg+xml,${svg}") ${cursorSize/2} ${cursorSize/2}, auto`;
    } else {
      // Кастомный черный крестик через SVG
      const crosshairSize = Math.max(20, Math.ceil(visualSize * 1.5));
      const center = crosshairSize / 2;
      const lineLength = crosshairSize * 0.4;
      const lineWidth = 2;
      const svg = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${crosshairSize}" height="${crosshairSize}">
        <line x1="${center}" y1="${center - lineLength}" x2="${center}" y2="${center + lineLength}" stroke="#000000" stroke-width="${lineWidth}" stroke-linecap="round"/>
        <line x1="${center - lineLength}" y1="${center}" x2="${center + lineLength}" y2="${center}" stroke="#000000" stroke-width="${lineWidth}" stroke-linecap="round"/>
        <circle cx="${center}" cy="${center}" r="${Math.max(2, visualSize / 2)}" fill="none" stroke="#000000" stroke-width="1" opacity="0.5"/>
      </svg>`);
      // Hotspot курсора в центре крестика
      return `url("data:image/svg+xml,${svg}") ${center} ${center}, auto`;
    }
  }, [layers.base, isDragging, cursorType, tool, brushSize, pencilSize, eraserSize, spraySize, scale]);

  // Обработка горячих клавиш
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []); // Функции undo/redo используют useRef, поэтому зависимости не нужны

  // Синхронизация historyRef с состоянием
  useEffect(() => {
    historyRef.current = { history, historyIndex };
  }, [history, historyIndex]);

  // Перерисовка при изменении состояния
  useEffect(() => {
    redrawCanvas();
  }, [scale, offset, layers]);

  // Инициализация canvas размеров (только если изображение не загружено)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && !layers.base) {
      canvas.width = 800;
      canvas.height = 600;
    }
  }, [layers.base]);

  // Проверка сохраненного состояния при загрузке компонента
  useEffect(() => {
    const savedState = localStorage.getItem('coloringAppState');
    if (savedState && !layers.base) {
      // Показываем диалог подтверждения
      const shouldLoad = window.confirm('Найдено сохраненное состояние. Загрузить его?');
      if (shouldLoad) {
        loadStateFromLocalStorage();
      } else {
        // Удаляем сохраненное состояние, если пользователь не хочет его загружать
        localStorage.removeItem('coloringAppState');
      }
    }
  }, []); // Выполняется только при монтировании компонента

return (
    <div className="coloring-app">
      <div className="app-header">
        <div class="logo">          
          <svg width="111" height="45" viewBox="0 0 111 45" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M70.3934 34.2397L73.9 24.5159H77.5133L72.094 38.5965C71.5804 39.9248 71.0843 40.9961 70.6062 41.8108C70.128 42.6432 69.5609 43.2543 68.9056 43.644C68.268 44.0336 67.4179 44.2285 66.3553 44.2285C66.0897 44.2285 65.8237 44.2021 65.558 44.149V41.1733C65.8237 41.2264 66.0897 41.2528 66.3553 41.2528C66.7095 41.2528 67.0019 41.1641 67.2321 40.987C67.48 40.8099 67.7015 40.5269 67.8962 40.1374C68.1087 39.7478 68.3389 39.234 68.5868 38.5965L62.636 24.5159H66.5681L70.3934 34.2397Z" fill="#1A1A1A"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M29.0346 24.1971C30.2389 24.1971 31.2838 24.3744 32.1694 24.7287C33.0726 25.0829 33.7724 25.6588 34.2683 26.4558C34.7641 27.235 35.0119 28.2889 35.0119 29.617V35.0098C35.0119 35.7536 35.0207 36.4094 35.0384 36.9761C35.0738 37.5251 35.1273 38.0653 35.1981 38.5965H31.9301C31.8593 38.2069 31.8153 37.9145 31.7976 37.7197V36.7899C31.4256 37.3567 30.8409 37.8527 30.0439 38.2778C29.247 38.7028 28.2372 38.9153 27.0152 38.9153C26.0058 38.9153 25.1469 38.7292 24.4385 38.3573C23.7301 37.9677 23.19 37.4363 22.8181 36.7634C22.4461 36.0904 22.26 35.3284 22.26 34.4782C22.2601 33.0615 22.6763 31.9988 23.5086 31.2905C24.3587 30.5821 25.6251 30.2281 27.3075 30.2281H31.5053V29.564C31.5053 28.6431 31.2928 27.9961 30.8677 27.6242C30.4604 27.2523 29.8046 27.0668 28.9014 27.0668C28.1045 27.0669 27.5291 27.2083 27.1749 27.4916C26.8208 27.7573 26.6171 28.0937 26.5639 28.501H23.0573C23.1105 27.6863 23.367 26.9601 23.8274 26.3225C24.3056 25.6673 24.979 25.1536 25.8468 24.7817C26.7323 24.3921 27.795 24.1971 29.0346 24.1971ZM27.9715 32.6186C27.2987 32.6186 26.7672 32.7783 26.3777 33.0971C26.0058 33.3982 25.8203 33.8144 25.8203 34.3457C25.8203 34.9123 26.0147 35.3639 26.4042 35.7003C26.7938 36.0368 27.3609 36.2054 28.1048 36.2054C28.6715 36.2053 29.2116 36.1079 29.7252 35.9131C30.2565 35.7006 30.6815 35.3818 31.0003 34.9568C31.3367 34.514 31.5053 33.9562 31.5053 33.2833V32.6186H27.9715Z" fill="#1A1A1A"/>
          <path d="M9.61705 30.4667L15.249 20H19.1016V38.5965H15.1165V26.7746L10.7066 34.8772H8.34194L3.95857 26.7746V38.5965H0V20H3.95857L9.61705 30.4667Z" fill="#1A1A1A"/>
          <path d="M44.9798 24.1971C45.3163 24.1971 45.5998 24.2147 45.8301 24.2501C46.078 24.2856 46.2378 24.3214 46.3086 24.3569V27.4651C46.1315 27.3943 45.9007 27.332 45.6173 27.2789C45.3517 27.2081 44.9887 27.1729 44.5285 27.1729C43.82 27.1729 43.2441 27.3231 42.8013 27.6242C42.3763 27.9252 42.0663 28.3239 41.8715 28.8198C41.6767 29.2979 41.5793 29.8205 41.5792 30.3871V38.5965H38.0992V24.5159H41.287V26.6148C41.4818 26.2075 41.7478 25.8265 42.0843 25.4722C42.4207 25.1181 42.837 24.8168 43.3329 24.5689C43.8287 24.321 44.3777 24.1971 44.9798 24.1971Z" fill="#1A1A1A"/>
          <path d="M51.9308 30.3076L57.138 24.5159H61.1496L55.7568 30.5469L61.6811 38.5965H57.643L53.4982 32.8578L51.9308 34.5319V38.5965H48.4508V20H51.9308V30.3076Z" fill="#1A1A1A"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M83.8085 22.6652C84.3449 9.68249 95.4664 0 109.117 0C109.96 0 110.209 0.464684 110.209 1.18542C110.209 3.85024 109.74 6.50557 108.82 9.09453C108.772 9.23678 108.667 9.36006 108.523 9.43593L104.5 11.5602H106.377C106.377 11.5602 106.396 11.5602 106.406 11.5602C106.636 11.5602 107.009 11.5602 107.259 11.949C107.373 12.1387 107.364 12.499 107.259 12.6982C102.842 20.5599 94.3552 25.4628 85.0825 25.5197C84.0096 27.312 82.8889 30.4984 82.8889 30.4984C82.544 31.3519 81.1167 31.0579 81.4903 29.9674C83.9905 23.329 87.621 18.502 92.7651 13.95C93.1578 13.5991 92.6885 12.9542 92.1903 13.3525C87.736 16.8614 86.1458 18.9003 83.8085 22.6747V22.6652Z" fill="#1A1A1A"/>
          </svg>
        </div>
        <div className="header-actions">
          <div className="header-actions-flex">
            <button 
              onClick={undo} 
              disabled={historyIndex < 0}
              title="Отменить (Ctrl+Z)"
              className="header-icon-button"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5H11C14.3137 5 17 7.68629 17 11C17 14.3137 14.3137 17 11 17H1M1 5L5 1M1 5L5 9" stroke="#ADADAD" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>          
            </button>
            <button 
              onClick={redo} 
              disabled={historyIndex >= history.length - 1}
              title="Повторить (Ctrl+Y)"
              className="header-icon-button"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 5H7C3.68629 5 1 7.68629 1 11C1 14.3137 3.68629 17 7 17H17M17 5L13 1M17 5L13 9" stroke="#ADADAD" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div className="instructions">
              <div className="instructions-block">Рисовать - <span>ЛКМ</span></div>
              <div className="instructions-block">Перемещение - <span>СКМ или Ctrl+ЛКМ</span></div>
              <div className="instructions-block">Масштабирование - <span>колесо мыши</span></div>
            </div>
          </div>
          <div className="header-actions-flex">
            <button 
              onClick={clearDrawing} 
              disabled={!layers.drawing}
              title="Очистить"
              className="header-icon-button header-icon-button-long"
            >
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.66667 1H14.3333M1 4.33333H21M18.7778 4.33333L17.9986 16.0214C17.8817 17.775 17.8232 18.6518 17.4445 19.3167C17.111 19.902 16.608 20.3725 16.0018 20.6663C15.3133 21 14.4346 21 12.6771 21H9.32295C7.56545 21 6.6867 21 5.99815 20.6663C5.39195 20.3725 4.88899 19.902 4.55554 19.3167C4.17679 18.6518 4.11834 17.775 4.00143 16.0214L3.22222 4.33333" stroke="#DA563A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Очистить
            </button>
            <button 
              onClick={saveImage} 
              disabled={!image}
              title="Скачать"
              className="header-icon-button header-icon-button-long"
            >            
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 19H1M16 9L10 15M10 15L4 9M10 15V1" stroke="#DA563A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>      
              Сохранить
            </button>
            <button 
              onClick={saveStateToLocalStorage} 
              disabled={!layers.base}
              title="Сохранить"
              className="header-icon-button header-icon-button-long"
            >
              <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5.00022L10.8845 2.76913C10.5634 2.12702 10.4029 1.80595 10.1634 1.57138C9.95158 1.36395 9.69632 1.20619 9.41607 1.10953C9.09916 1.00022 8.74021 1.00022 8.02229 1.00022H4.2C3.0799 1.00022 2.51984 1.00022 2.09202 1.2182C1.71569 1.40995 1.40973 1.71591 1.21799 2.09223C1 2.52006 1 3.08011 1 4.20022V5.00022M1 5.00022H16.2C17.8802 5.00022 18.7202 5.00022 19.362 5.3272C19.9265 5.61482 20.3854 6.07376 20.673 6.63824C21 7.27998 21 8.12006 21 9.80022V14.2002C21 15.8804 21 16.7205 20.673 17.3622C20.3854 17.9267 19.9265 18.3856 19.362 18.6732C18.7202 19.0002 17.8802 19.0002 16.2 19.0002H5.8C4.11984 19.0002 3.27976 19.0002 2.63803 18.6732C2.07354 18.3856 1.6146 17.9267 1.32698 17.3622C1 16.7205 1 15.8804 1 14.2002V5.00022ZM8 12.0002L10 14.0002L14.5 9.50022" stroke="#DA563A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Скачать       
            </button>
          </div>
        </div>
      </div>
      
      <div className="app-layout">
        {/* Левая панель инструментов */}
        <div className="left-sidebar">
          <div className="toolbar">

            {(tool === 'brush' || tool === 'pencil' || tool === 'spray') && (
              <div className="tool-section">
                <div className="color-picker-section">
                  {/* Стандартная палитра цветов (как в браузере) */}
                  <div className="standard-color-picker">
                      {/* Квадрат насыщенности и яркости */}
                      <div 
                        className="sv-picker"
                        style={{
                          background: `linear-gradient(to top, #000 0%, transparent 100%),
                                      linear-gradient(to right, #fff 0%, ${hsvToRgb(hue, 1, 1)} 100%)`
                        }}
                        onClick={handleAdvancedColorPick}
                        onMouseMove={(e) => {
                          if (e.buttons === 1) {
                            handleAdvancedColorPick(e);
                          }
                        }}
                      >
                        <div 
                          className="sv-picker-indicator"
                          style={{
                            left: `${saturation * 100}%`,
                            top: `${(1 - value) * 100}%`
                          }}
                        />
                      </div>

                      {/* Ползунок оттенка */}
                      <div className="hue-slider-container">
                        <input
                          type="range"
                          min="0"
                          max="360"
                          value={hue}
                          onChange={(e) => handleHueChange(Number(e.target.value))}
                          className="hue-slider"
                          style={{
                            background: `linear-gradient(to right, 
                              #ff0000 0%, #ffff00 17%, #00ff00 33%, 
                              #00ffff 50%,#0000ff 67%, #ff00ff 83%, #ff0000 100%)`
                          }}
                        />
                      </div>

                      {/* Нижняя панель с hex и пипеткой */}
                      <div className="color-picker-footer">
                        <div className="hex-input-container">
                          <input
                            type="text"
                            value={color.toUpperCase()}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                if (value.length === 7) {
                                  setColor(value);
                                }
                              }
                            }}
                            className="hex-input"
                            placeholder="#000000"
                            maxLength={7}
                          />
                        </div>
                        <div className="current-color-display" style={{ backgroundColor: color }}>
                          <span className="color-hex">{color.toUpperCase()}</span>
                        </div>
                        <button
                          className="eyedropper-button"
                          onClick={useEyedropper}
                          title="Пипетка - выбрать цвет с экрана"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                            <line x1="2" y1="22" x2="22" y2="2"/>
                            <line x1="16" y1="2" x2="22" y2="2"/>
                            <line x1="22" y1="2" x2="22" y2="8"/>
                          </svg>
                        </button>
                      </div>

                      {/* Предустановленные цвета */}
                      <div className="color-palette">
                        {colorPalette.map((paletteColor, index) => (
                          <button
                            key={index}
                            className={`palette-color ${color === paletteColor ? 'active' : ''}`}
                            style={{ backgroundColor: paletteColor }}
                            onClick={() => setColor(paletteColor)}
                            title={paletteColor}
                          />
                        ))}
                      </div>
                    </div>
                </div>
              </div>
            )}
              
            <div className="tool-section">
              <h3>Инструменты</h3>
              <div className="tool-buttons">
                <button 
                  className={tool === 'brush' ? 'active' : ''}
                  onClick={() => setTool('brush')}
                  title="Кисточка"
                ><svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.66656 8.22323L9.81471 11.3714M5.81229 16.3504C4.67326 17.4895 2.49992 17.2047 0.833252 17.2047C1.6875 15.538 0.548473 13.3647 1.6875 12.2257C2.82653 11.0866 4.67326 11.0866 5.81229 12.2257C6.95132 13.3647 6.95132 15.2114 5.81229 16.3504ZM9.10125 12.1419L16.7155 3.9185C17.3862 3.19419 17.3645 2.06937 16.6665 1.37138C15.9685 0.673386 14.8437 0.651761 14.1194 1.32241L5.89603 8.93666C5.47108 9.33013 5.25861 9.52687 5.13469 9.73669C4.83754 10.2398 4.82558 10.8618 5.10318 11.376C5.21894 11.5904 5.4237 11.7952 5.83321 12.2047C6.24272 12.6142 6.44747 12.819 6.6619 12.9347C7.17609 13.2123 7.79809 13.2004 8.30123 12.9032C8.51105 12.7793 8.70778 12.5668 9.10125 12.1419Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>Кисточка
                </button>
                <button 
                  className={tool === 'pencil' ? 'active' : ''}
                  onClick={() => setTool('pencil')}
                  title="Карандаш"
                ><svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.2499 16.5237H9.58328M0.833252 16.9403L5.45764 15.1617C5.75343 15.048 5.90132 14.9911 6.03968 14.9168C6.16259 14.8508 6.27975 14.7747 6.38995 14.6892C6.51402 14.5929 6.62606 14.4809 6.85015 14.2568L16.2499 4.857C17.1704 3.93653 17.1704 2.44414 16.2499 1.52367C15.3295 0.603195 13.8371 0.603194 12.9166 1.52367L3.51682 10.9234C3.29273 11.1475 3.18069 11.2596 3.08441 11.3836C2.9989 11.4938 2.92275 11.611 2.85678 11.7339C2.78251 11.8723 2.72563 12.0202 2.61186 12.316L0.833252 16.9403ZM0.833252 16.9403L2.54835 12.4811C2.67108 12.162 2.73245 12.0025 2.8377 11.9294C2.92969 11.8655 3.0435 11.8414 3.1535 11.8624C3.27936 11.8864 3.40024 12.0073 3.64199 12.2491L5.52456 14.1316C5.76631 14.3734 5.88719 14.4942 5.91122 14.6201C5.93223 14.7301 5.90807 14.8439 5.84421 14.9359C5.77112 15.0412 5.61158 15.1025 5.29248 15.2253L0.833252 16.9403Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>Карандаш
                </button>                
                <button 
                  className={tool === 'spray' ? 'active' : ''}
                  onClick={() => setTool('spray')}
                  title="Распылитель"
                >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.49992 2.49998C7.96016 2.49998 8.33325 2.12688 8.33325 1.66665C8.33325 1.20641 7.96016 0.833313 7.49992 0.833313C7.03968 0.833313 6.66659 1.20641 6.66659 1.66665C6.66659 2.12688 7.03968 2.49998 7.49992 2.49998Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M7.49992 8.33331C7.96016 8.33331 8.33325 7.96022 8.33325 7.49998C8.33325 7.03974 7.96016 6.66665 7.49992 6.66665C7.03968 6.66665 6.66659 7.03974 6.66659 7.49998C6.66659 7.96022 7.03968 8.33331 7.49992 8.33331Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M7.49992 14.1666C7.96016 14.1666 8.33325 13.7935 8.33325 13.3333C8.33325 12.8731 7.96016 12.5 7.49992 12.5C7.03968 12.5 6.66659 12.8731 6.66659 13.3333C6.66659 13.7935 7.03968 14.1666 7.49992 14.1666Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13.3333 2.49998C13.7935 2.49998 14.1666 2.12688 14.1666 1.66665C14.1666 1.20641 13.7935 0.833313 13.3333 0.833313C12.873 0.833313 12.4999 1.20641 12.4999 1.66665C12.4999 2.12688 12.873 2.49998 13.3333 2.49998Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13.3333 8.33331C13.7935 8.33331 14.1666 7.96022 14.1666 7.49998C14.1666 7.03974 13.7935 6.66665 13.3333 6.66665C12.873 6.66665 12.4999 7.03974 12.4999 7.49998C12.4999 7.96022 12.873 8.33331 13.3333 8.33331Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13.3333 14.1666C13.7935 14.1666 14.1666 13.7935 14.1666 13.3333C14.1666 12.8731 13.7935 12.5 13.3333 12.5C12.873 12.5 12.4999 12.8731 12.4999 13.3333C12.4999 13.7935 12.873 14.1666 13.3333 14.1666Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1.66659 2.49998C2.12682 2.49998 2.49992 2.12688 2.49992 1.66665C2.49992 1.20641 2.12682 0.833313 1.66659 0.833313C1.20635 0.833313 0.833252 1.20641 0.833252 1.66665C0.833252 2.12688 1.20635 2.49998 1.66659 2.49998Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1.66659 8.33331C2.12682 8.33331 2.49992 7.96022 2.49992 7.49998C2.49992 7.03974 2.12682 6.66665 1.66659 6.66665C1.20635 6.66665 0.833252 7.03974 0.833252 7.49998C0.833252 7.96022 1.20635 8.33331 1.66659 8.33331Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1.66659 14.1666C2.12682 14.1666 2.49992 13.7935 2.49992 13.3333C2.49992 12.8731 2.12682 12.5 1.66659 12.5C1.20635 12.5 0.833252 12.8731 0.833252 13.3333C0.833252 13.7935 1.20635 14.1666 1.66659 14.1666Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>Распылитель
                </button>
                <button 
                  className={tool === 'eraser' ? 'active' : ''}
                  onClick={() => setTool('eraser')}
                  title="Ластик"
                ><svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.9644 9.79778L8.13105 3.96445M16.4644 16.4644H5.63105M8.07877 15.6834L15.3026 8.45954C16.2927 7.4695 16.7877 6.97448 16.9732 6.40365C17.1363 5.90155 17.1363 5.36068 16.9732 4.85857C16.7877 4.28775 16.2927 3.79273 15.3026 2.80269L15.1261 2.62621C14.1361 1.63616 13.6411 1.14114 13.0703 0.955672C12.5682 0.792527 12.0273 0.792527 11.5252 0.955672C10.9544 1.14114 10.4593 1.63616 9.46929 2.62621L2.62615 9.46935C1.6361 10.4594 1.14108 10.9544 0.955611 11.5252C0.792466 12.0273 0.792466 12.5682 0.955611 13.0703C1.14108 13.6411 1.6361 14.1362 2.62614 15.1262L3.18334 15.6834C3.47159 15.9716 3.61572 16.1158 3.78391 16.2188C3.93303 16.3102 4.0956 16.3776 4.26567 16.4184C4.45748 16.4644 4.6613 16.4644 5.06895 16.4644H6.19315C6.6008 16.4644 6.80463 16.4644 6.99644 16.4184C7.1665 16.3776 7.32907 16.3102 7.47819 16.2188C7.64639 16.1158 7.79051 15.9716 8.07877 15.6834Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>Ластик
                </button>
              </div>
              <div class="tool-section-margin"></div>
              <h3>Размер инструмента</h3>
              {tool === 'brush' && (
                <div className="size-control">
                  <input 
                    type="range" 
                    min="1" 
                    max="50"                    
                    className='custom-slider'
                    value={brushSize}
                    onChange={(e) => setBrushSize(Number(e.target.value))}
                  />
                  <span>{brushSize}px</span>
                </div>
              )}
              
              {tool === 'pencil' && (
                <div className="size-control">
                  <input 
                    type="range" 
                    min="1" 
                    max="10" 
                    value={pencilSize}
                    className='custom-slider'
                    onChange={(e) => setPencilSize(Number(e.target.value))}
                  />
                  <span>{pencilSize}px</span>
                </div>
              )}
              
              {tool === 'eraser' && (
                <div className="size-control">
                  <input 
                    type="range" 
                    min="1" 
                    max="50" 
                    value={eraserSize}
                    className='custom-slider'
                    onChange={(e) => setEraserSize(Number(e.target.value))}
                  />
                  <span>{eraserSize}px</span>
                </div>
              )}
              
              {tool === 'spray' && (
                <div className="size-control">
                  <input 
                    type="range" 
                    min="5" 
                    max="100" 
                    value={spraySize}
                    className='custom-slider'
                    onChange={(e) => setSpraySize(Number(e.target.value))}
                  />
                  <span>{spraySize}px</span>
                </div>
              )}
            </div>

            <div className="tool-section">
              <h3>Курсор</h3>
              <div className="cursor-type-buttons">
                <button 
                  className={cursorType === 'crosshair' ? 'active' : ''}
                  onClick={() => setCursorType('crosshair')}
                  title="Крестик"
                >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.66659 0.833313V12.5M0.833252 6.66665H12.4999" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>Крестик
                </button>
                <button 
                  className={cursorType === 'circle' ? 'active' : ''}
                  onClick={() => setCursorType('circle')}
                  title="Круг"
                >
                <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.16658 17.5C13.769 17.5 17.4999 13.769 17.4999 9.16665C17.4999 4.56427 13.769 0.833313 9.16658 0.833313C4.56421 0.833313 0.833252 4.56427 0.833252 9.16665C0.833252 13.769 4.56421 17.5 9.16658 17.5Z" stroke="#DA563A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>Круг
                </button>
              </div>
            </div>

            <div className="tool-section">
              <h3>Масштаб</h3>
              <div className="zoom-control">
                <input 
                  type="range" 
                  min="0.1" 
                  max="4" 
                  step="0.1" 
                  value={scale}
                  className='custom-slider'
                  onChange={(e) => setScale(Number(e.target.value))}
                  disabled={!layers.base}
                />
                <div className="zoom-info">
                  <span>{Math.round(scale * 100)}%</span>
                  <button onClick={() => setScale(1)} disabled={!layers.base}>100%</button>
                </div>
              </div>
            </div>

            <div className="tool-section">
              <h3>Действия</h3>
              <div className="action-buttons">
                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload} 
                  style={{display: 'none'}} 
                />
                <input 
                  ref={loadFileInputRef}
                  type="file" 
                  accept=".json" 
                  onChange={handleProjectImport} 
                  style={{display: 'none'}} 
                />
                <button onClick={loadStateFromLocalStorage}>
                  📂 Загрузить проект
                </button>
                <button onClick={exportProject} disabled={!layers.base}>
                  📤 Экспорт в файл
                </button>
                <button onClick={() => loadFileInputRef.current?.click()} disabled={!layers.base}>
                  📥 Импорт из файла
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Правая область с canvas */}
        <div className="canvas-area">
          
          <div className="canvas-container">
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onWheel={handleWheel}
              style={{ 
                cursor: cursorStyle
              }}
            />
            {!image && (
              <div className="placeholder" onClick={() => fileInputRef.current?.click()}>
                Кликните здесь, чтобы загрузить изображение для раскраски
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColoringApp;